import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../context/AuthContext';
import { LockOrderContext } from '../context/LockOrderContext';
import styles from '../styles/ErrorPage.module.css';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import URLSearchParamsLowerCaseKey from '../models/URLSearchParamsLowerCaseKey';
import { Grid, Typography, Button, Box } from '@mui/material';
import { useLogger } from '../hooks/useLogger';
import { SettingsContext } from '../context/SettingsContext';

interface IErrorPageProps {
  error?: any | null;
  errorInfo?: React.ErrorInfo | null;
  handleClick?: () => void;
}

function ErrorPage({ error, errorInfo, handleClick }: IErrorPageProps) {
  const { authState } = useOktaAuth();
  const logger = useLogger();

  const { requestURLParameters, systemReadOnlyMode } = useContext(AuthContext);
  const { designID, isQuote } = useContext(LockOrderContext);
  const settingsContext = useContext(SettingsContext);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [errorTitle, setErrorTitle] = useState<string | undefined>(undefined);
  const [traceID, setTraceID] = useState<string | undefined>(undefined);
  const [instance, setInstance] = useState<string | undefined>(
    requestURLParameters
  );
  const [simpleView, setSimpleView] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isChartedCustomerMessage = (value: string | undefined) => {
    return (
      value && value.includes('{BRADY_EXCEPTION_CHARTED_CUSTOMER_MESSAGE}')
    );
  };

  useEffect(() => {
    if (error) {
      //handle unknown error passed in props, from ErrorBoundary
      logger.UI.error(`Error page - Unknown Error: `, error);

      setErrorTitle(`Unknown Error`);
      setErrorMessage(
        'An error occurred while loading this page. If you continue to see this message, please contact support.'
      );
      setSimpleView(true);
      return;
    }

    if (location.state) {
      logger.UI.error(`Error page - location.state: `, location.state);

      //error passed in location state, handled exception
      let message = location.state.message ? location.state.message : '';
      let title = location.state.title ? location.state.title : '';

      //use passed message and title if present
      if (location.state.problemModel) {
        message = message
          ? message
          : `There was a problem loading the page. ${
              location.state.problemModel.detail
                ? location.state.problemModel.detail
                : ''
            }`;

        title = title
          ? title
          : `${location.state.problemModel.title} (${location.state.problemModel.status})`;

        if (location.state.problemModel.instance)
          setInstance(location.state.problemModel.instance);

        if (location.state.problemModel.traceID)
          setTraceID(location.state.problemModel.traceID);
      } else {
        switch (location.state.status) {
          case 400:
            setErrorTitle('Bad Request (400)');
            setErrorMessage(
              'There was a problem with the request that prevented the resource from loading.'
            );
            break;
          case 401:
            setErrorTitle(
              systemReadOnlyMode ? 'URL Not Recognized' : 'Unauthorized (401)'
            );
            setErrorMessage(
              systemReadOnlyMode
                ? 'Unable to authenticate access with the provided link. Please check your link and try again.'
                : 'There was a problem with the request that prevented the resource from loading.'
            );
            break;
          case 404:
            setErrorTitle('Bad Request (404)');
            setErrorMessage(
              'There was a problem loading the page. The requested resources was not found.'
            );
            break;
          case 500:
            setErrorTitle('Bad Request (500)');
            setErrorMessage(
              'There was a problem loading the page due to a server error.'
            );
            break;
          default:
            setErrorTitle(`Unknown Error (${location.state.status})`);
            setErrorMessage('An error occurred while loading this page.');
        }
      }

      if (isChartedCustomerMessage(message)) {
        logger.UI.log(`Developer Error Message: ${message}`);
      }

      setErrorMessage(message);
      setErrorTitle(title);
      setSimpleView(location.state.simpleView === true);
    }

    //override instance if callback is present
    let urlParams = new URLSearchParamsLowerCaseKey(requestURLParameters);
    let callback = urlParams.get('code');
    if (callback) setInstance('');
  }, []);

  useEffect(() => {
    if (!systemReadOnlyMode && authState && !authState.isAuthenticated) {
      if (authState.error) {
        logger.AI.error(
          `Error page - Auth Error Name: ${authState.error.name} Message: ${authState.error.message} Cause: ${authState.error.cause}`
        );
      }
      logger.AI.error(
        `Error page - isAuthenticated: ${authState.isAuthenticated}, userId: ${authState.accessToken?.claims.sub}`
      );
      setErrorTitle('Not Authorized');
      setErrorMessage("You don't have permissions to access this site.");
      setInstance('');
      setTraceID('');
    }
  }, [authState]);

  useEffect(() => {
    const logs = logger.UI.get();

    if (error) {
      if (typeof error === 'string') {
        logger.AI.error(
          `Unhandled Exception in UI: ${error}; ${errorInfo?.componentStack?.toString()}`
        );
      } else if (error instanceof Error) {
        logger.AI.error(
          `Unhandled Exception in UI: ${
            error.message
          }; ${errorInfo?.componentStack?.toString()}`
        );
      } else {
        logger.AI.error(
          `Unhandled Exception in UI: ${JSON.stringify(
            error
          )}; ${errorInfo?.componentStack?.toString()}`
        );
      }
    }
    if (logs && logs.length > 0) {
      logger.UI.uploadTraceLogs();
    }
  }, []);

  //Problem model
  // {
  //   "type": "https://tools.ietf.org/html/rfc7231#section-6.5.4",
  //   "title": "Not Found",
  //   "status": 404,
  //   "traceId": "00-e8ebc1c4d172f309c2c4cc5ac2a93d6e-c6f739ed00ccea24-00"

  //Situation Dependend Properites
  //    "detail": "description of error or causes"
  //    "instance": "GET/POST/PUT  url + querystring"
  //    "stacktrace": only in dev/staging and only unhandled exceptions
  // }

  const onClick = () => {
    if (handleClick) {
      handleClick();
    }
  };

  const CustomMessageHandler = () => {
    //Tags for custom messages set in api
    // const customMessage = (designID === 'quote' || isQuote) ?
    // 'Please close this window and retrieve the SAP quote or order in order change.  Add the charted customer to the header partners, reject the Safekey part line item previously entered and enter the Safekey part on a new line with a quantity of 1 and hit save'
    // :'Please close this window and retrieve the SAP order in order change.  Add the charted customer to header partners and hit the "Retrieve Design" button on the line item additional Data B tab to get to the lock portal.'

    const customMessage = `Please close this window and retrieve the SAP ${
      settingsContext.featureManagement.quoting ? 'quote or ' : ''
    } order in order change.  Add the charted customer to the header partners, reject the Safekey part line item previously entered and enter the Safekey part on a new line with a quantity of 1 and hit save`;

    if (isChartedCustomerMessage(errorMessage)) {
      return (
        <>
          <Typography
            className={styles.message}
            variant='body1'
          >
            There was a problem loading the page.
          </Typography>
          <br />
          <Typography className={styles.messageInstructions}>
            ****A Charted Customer has not been added to the{' '}
            {settingsContext.featureManagement.quoting &&
            (designID === 'quote' || isQuote)
              ? 'Quote'
              : 'Order'}{' '}
            in SAP****
          </Typography>
          <br />
          <Typography className={styles.messageInstructions}>
            {customMessage}
          </Typography>
        </>
      );
    }

    return (
      <Typography className={styles.message}>
        {errorMessage
          ? errorMessage
          : 'There was an unexpected error. If this continues, please contact a system administrator.'}
      </Typography>
    );
  };

  return !authState ? (
    <Navigate to='/' />
  ) : (
    <Box
      className={styles.page}
      component='section'
    >
      <Grid
        container
        sx={{
          justifyContent: 'center',
          width: 1
        }}
      >
        <Grid
          size={{
            xs: 12,
            md: 8,
            lg: 6
          }}>
          <Box
            className={styles.container}
            component='article'
          >
            <Typography
              className={styles.title}
              variant='h1'
            >
              {errorTitle ? errorTitle : 'Error'}
            </Typography>
            <Box sx={{
              my: 2
            }} /> {/* Adds vertical margin */}
            <CustomMessageHandler />
            <br />
            {!simpleView && (
              <>
                <Typography
                  className={styles.message}
                  variant='body1'
                >
                  {instance}
                </Typography>
                <Typography
                  className={styles.message}
                  variant='body1'
                >
                  {traceID}
                </Typography>
              </>
            )}
            <Button
              className={styles.button}
              sx={{ mt: 4 }}
              onClick={() => {
                onClick();
                navigate('/customer-search');
              }}
            >
              Go to Customer Search
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ErrorPage;
